import React from "react";
import styled from "styled-components";
import { FiFileText, FiX } from "react-icons/fi";
import { colors } from "./colors";
import { formatDay, formatNumber } from "./format";

/** The page changes the drops are filtered by, with how many drops are on the URLs they touched. */
export default function ChangeWindowBar({ window, entries, matchCount, onClear }) {
    const isDay = window.start === window.end;
    const total = entries.reduce((sum, entry) => sum + entry.urlCount, 0);
    const label = isDay ? formatDay(window.start) : `${formatDay(window.start)} – ${formatDay(window.end)}`;

    return (
        <Bar>
            <Head>
                <Icon aria-hidden />
                <Text>
                    <strong>{formatNumber(total)} page changes</strong> detected {isDay ? "on" : "between"} {label}
                    <Muted>{" · "}{matchCount > 0 ? `${matchCount} ${matchCount === 1 ? "drop" : "drops"} on affected URLs` : "no drops on affected URLs"}</Muted>
                </Text>
                <ClearButton type="button" onClick={onClear}><FiX aria-hidden />Clear</ClearButton>
            </Head>
            <Entries aria-label="Changes in this window">
                {entries.map((entry) => (
                    <Entry key={entry.id}>
                        <Field>{entry.field}</Field>
                        <Pattern>{entry.urlPattern}</Pattern>
                        <Faint>· {formatNumber(entry.urlCount)} {entry.urlCount === 1 ? "URL" : "URLs"}</Faint>
                        {!isDay && <Faint>· {formatDay(entry.date)}</Faint>}
                    </Entry>
                ))}
            </Entries>
        </Bar>
    );
}

const Bar = styled.div`
    padding: 12px 20px;
    border-bottom: 1px solid ${colors.line};
    background: ${colors.pageChange}0d;
`;

const Head = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 12px;
`;

const Icon = styled(FiFileText)`
    flex-shrink: 0;
    font-size: 16px;
    color: ${colors.pageChange};
`;

const Text = styled.p`
    margin: 0;
    font-size: 13px;
    color: ${colors.ink};
    strong { font-weight: 600; }
`;

const Muted = styled.span`
    color: ${colors.muted};
`;

const ClearButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 28px;
    margin-left: auto;
    border: none;
    border-radius: 6px;
    background: none;
    padding: 0 8px;
    font-size: 13px;
    font-weight: 500;
    color: ${colors.muted};
    cursor: pointer;
    &:hover { background: ${colors.surface}; color: ${colors.ink}; }
`;

const Entries = styled.ul`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: 8px 0 0;
    padding: 0 0 0 28px;
    list-style: none;
`;

const Entry = styled.li`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border-radius: 6px;
    border: 1px solid ${colors.line};
    background: ${colors.surface};
    padding: 4px 8px;
    font-size: 12px;
`;

const Field = styled.span`
    font-weight: 500;
    color: ${colors.ink};
`;

const Pattern = styled.span`
    font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, monospace;
    color: ${colors.muted};
`;

const Faint = styled.span`
    color: ${colors.subtle};
`;
